import { useQuery } from '@tanstack/react-query';
import { Activity, BarChart3, CheckCircle, ShieldCheck, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardHeader, CardTitle, MetricCard, PageHeader, ProductIntro, ProgressRing, DonutChart, SectionEmptyState } from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';

interface RiskSummary {
  totalEvents: number;
  allowedCount: number;
  challengeCount: number;
  reviewCount: number;
  blockedCount: number;
  lowRiskCount: number;
  mediumRiskCount: number;
  highRiskCount: number;
  criticalRiskCount: number;
  averageTrustScore: number;
  averageRiskScore: number;
}

interface TrustEvent {
  transactionId: string;
  productCode: string;
  mobileNumberMasked: string;
  operator: string;
  countryCode: string;
  riskScore: number;
  trustScore: number;
  riskLevel: string;
  decision: string;
  /** positiveFactors stored in reasonCodes for TRUST events */
  reasonCodes: string[];
  /** breakdown stored in signals */
  signals?: Record<string, unknown>;
  createdAt: string;
}

interface PageDto<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

async function fetchRiskSummary(): Promise<RiskSummary> {
  const res = await api.get<ResponseEnvelope<RiskSummary>>(Endpoints.me.riskSummary, {
    params: { productCode: 'TRUST' },
  });
  return unwrap(res);
}
async function fetchTrustEvents(): Promise<PageDto<TrustEvent>> {
  const res = await api.get<ResponseEnvelope<PageDto<TrustEvent>>>(Endpoints.me.riskEvents, {
    params: { productCode: 'TRUST', page: 0, size: 15 },
  });
  return unwrap(res);
}

function trustBand(score: number) {
  if (score >= 70) return { label: 'Allow',   color: '#10b981', textColor: 'text-emerald-600 dark:text-emerald-400' };
  if (score >= 40) return { label: 'Review',  color: '#f59e0b', textColor: 'text-amber-600 dark:text-amber-400'   };
  return               { label: 'Block',   color: '#ef4444', textColor: 'text-rose-600 dark:text-rose-400'     };
}

function confidenceTone(c: string): 'success' | 'warning' | 'neutral' {
  if (c === 'HIGH')   return 'success';
  if (c === 'MEDIUM') return 'warning';
  return 'neutral';
}

function decisionTone(d: string): 'success' | 'warning' | 'neutral' | 'danger' {
  switch (d) {
    case 'ALLOW':     return 'success';
    case 'CHALLENGE': return 'warning';
    case 'REVIEW':    return 'neutral';
    case 'BLOCK':     return 'danger';
    default:          return 'neutral';
  }
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function factorLabel(code: string): string {
  const map: Record<string, string> = {
    HIGH_VERIFICATION_SUCCESS_RATE: 'High success rate',
    CLEAN_VERIFICATION_HISTORY:     'Clean history',
    VERIFICATION_RECOVERED:         'Recovered from Stuck',
    RECENTLY_VERIFIED_7D:           'Verified in last 7 days',
    RECENTLY_VERIFIED_30D:          'Verified in last 30 days',
    LONG_STANDING_IDENTITY_90D:     'Identity >90 days old',
    ESTABLISHED_IDENTITY_30D:       'Identity >30 days old',
    STABLE_DEVICE:                  'Stable device',
    STABLE_OPERATOR:                'Stable operator',
    STABLE_COUNTRY:                 'Stable country',
    LOW_VERIFICATION_SUCCESS_RATE:  'Low success rate',
    UNRESOLVED_STUCK_JOURNEY:       'Unresolved journey',
    MULTI_DEVICE_USAGE:             'Multiple devices',
    OPERATOR_CHANGE_HISTORY:        'Operator change history',
    MULTI_COUNTRY_HISTORY:          'Multiple countries',
    CURRENTLY_UNRESOLVED:           'Currently unresolved',
    INVALID_MOBILE:                 'Invalid mobile',
  };
  return map[code] ?? code.replace(/_/g, ' ').toLowerCase();
}

export function ClientTrustScoresPage() {
  const summaryQ = useQuery({ queryKey: ['me', 'risk-summary', 'TRUST'],  queryFn: fetchRiskSummary,  staleTime: 60_000 });
  const eventsQ  = useQuery({ queryKey: ['me', 'trust-events'],  queryFn: fetchTrustEvents,  staleTime: 60_000 });

  const s = summaryQ.data;
  const avgTrust = s ? Math.round(s.averageTrustScore) : 0;
  const { label: trustLabel, color: trustColor } = trustBand(avgTrust);

  const decisionSegments = s ? [
    { label: 'Allow',     value: s.allowedCount,   color: '#10b981' },
    { label: 'Challenge', value: s.challengeCount, color: '#f59e0b' },
    { label: 'Review',    value: s.reviewCount,    color: '#3b82f6' },
    { label: 'Block',     value: s.blockedCount,   color: '#ef4444' },
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Trust Scores"
        subtitle="Long-term mobile identity trust from Authmatech Trust assessments."
        badge={<Badge tone="trust">Trust</Badge>}
      />

      <ProductIntro
        icon={ShieldCheck}
        tone="trust"
        eyebrow="Customer Intelligence · Trust"
        title="One portable, explainable trust score for every mobile identity."
        points={['0–100 score', 'Allow / Review / Block', 'Fully explainable', 'Rewards good history']}
      >
        <p>
          <strong>Authmatech Trust</strong> produces a single <strong>0–100 score</strong> that sums up how
          established and reliable a mobile identity is — based on everything you’ve ever seen from it: its
          verification history, recovery journeys, and the devices, operators and countries linked to it — plus a
          confidence band and an <strong>Allow / Review / Block</strong> recommendation.
        </p>
        <p>
          Where Shield decides about one action right now, Trust answers the slower question: <em>over everything
          we’ve seen, how much should we trust this identity?</em> Use it to auto-approve your best customers,
          step up the uncertain ones, and hold the risky ones. Every point in the score is traceable to a
          documented rule — no black box.
        </p>
      </ProductIntro>

      {/* Trust hero */}
      <div
        className="relative overflow-hidden rounded-2xl px-6 py-5"
        style={{ background: 'linear-gradient(135deg, #083344 0%, #0e4f5c 60%, #0891b2 100%)' }}
      >
        <div className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 rounded-full bg-white/[0.04]" aria-hidden />
        <div className="relative flex flex-wrap items-center gap-8">
          <ProgressRing
            value={avgTrust}
            max={100}
            size={100}
            thickness={9}
            color={trustColor}
            label={String(avgTrust)}
            sublabel="avg trust"
          />
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/60">Average Trust Score</p>
            <p className="text-5xl font-extrabold tracking-tight text-white">{avgTrust}</p>
            <p className="mt-1 text-sm font-medium" style={{ color: trustColor }}>{trustLabel}</p>
          </div>
          {s && (
            <div className="ml-auto flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{s.totalEvents.toLocaleString()}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">Assessments</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">{Math.round(s.averageRiskScore)}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide text-white/60">Avg risk score</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard label="Total assessments" value={s?.totalEvents ?? 0} icon={ShieldCheck} tone="trust" loading={summaryQ.isLoading} />
        <MetricCard label="Avg trust score" value={avgTrust} subtitle={trustLabel} icon={Activity} tone="trust" loading={summaryQ.isLoading} />
        <MetricCard label="Avg risk score" value={s ? Math.round(s.averageRiskScore) : 0} subtitle="Lower is safer" icon={BarChart3} tone={s && s.averageRiskScore > 60 ? 'danger' : 'success'} loading={summaryQ.isLoading} />
      </div>

      {/* Score distribution + Decision donut */}
      {s && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Score Distribution</CardTitle></CardHeader>
            <div className="space-y-4">
              {[
                { range: '70–100', label: 'Allow',  count: s.lowRiskCount,      color: '#10b981' },
                { range: '40–69',  label: 'Review', count: s.mediumRiskCount + s.highRiskCount, color: '#f59e0b' },
                { range: '0–39',   label: 'Block',  count: s.criticalRiskCount, color: '#ef4444' },
              ].map(({ range, label, count, color }) => {
                const pct = s.totalEvents ? (count / s.totalEvents) * 100 : 0;
                return (
                  <div key={range}>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
                        <span className="font-medium text-fg">{label}</span>
                        <span className="text-fg-subtle">{range}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-fg tabular-nums">{count.toLocaleString()}</span>
                        <span className="text-fg-subtle">({pct.toFixed(0)}%)</span>
                      </div>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-muted">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="flex flex-col items-center">
            <CardHeader className="w-full"><CardTitle>Decision Distribution</CardTitle></CardHeader>
            {decisionSegments.some((d) => d.value > 0) ? (
              <DonutChart segments={decisionSegments} centerValue={s.totalEvents} centerLabel="total" size={160} thickness={22} />
            ) : (
              <SectionEmptyState title="No decisions yet" className="py-8" />
            )}
          </Card>
        </div>
      )}

      {/* Recent Trust assessments with factor breakdown */}
      <Card noPadding>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <CardTitle>Recent Trust Assessments</CardTitle>
          <Link to="/portal/risk-events?productCode=TRUST" className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {eventsQ.isLoading ? (
          <div className="divide-y divide-border">
            {[0,1,2,3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-4 px-5 py-3">
                <div className="h-3 w-32 rounded bg-surface-muted" />
                <div className="h-3 w-16 rounded bg-surface-muted ml-auto" />
                <div className="h-3 w-12 rounded bg-surface-muted" />
              </div>
            ))}
          </div>
        ) : !eventsQ.data?.content.length ? (
          <SectionEmptyState
            icon={ShieldCheck}
            title="No Trust assessments yet"
            description="Integrate the Trust API to start scoring mobile identities."
            action={
              <Link to="/portal/docs" className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
                View API docs →
              </Link>
            }
            className="py-12"
          />
        ) : (
          <div className="divide-y divide-border">
            {eventsQ.data.content.map((e) => {
              const { label: tLabel, textColor: tc } = trustBand(e.trustScore ?? 0);
              const posFactors = (e.reasonCodes ?? []).filter(c =>
                !['LOW_VERIFICATION_SUCCESS_RATE','UNRESOLVED_STUCK_JOURNEY','MULTI_DEVICE_USAGE','OPERATOR_CHANGE_HISTORY','MULTI_COUNTRY_HISTORY','CURRENTLY_UNRESOLVED','INVALID_MOBILE'].includes(c)
              );
              const riskFactors = (e.reasonCodes ?? []).filter(c =>
                ['LOW_VERIFICATION_SUCCESS_RATE','UNRESOLVED_STUCK_JOURNEY','MULTI_DEVICE_USAGE','OPERATOR_CHANGE_HISTORY','MULTI_COUNTRY_HISTORY','CURRENTLY_UNRESOLVED','INVALID_MOBILE'].includes(c)
              );
              const breakdown = e.signals as Record<string, number> | undefined;
              return (
                <div key={e.transactionId} className="px-5 py-4">
                  {/* Top row */}
                  <div className="flex flex-wrap items-start gap-3">
                    {/* Score */}
                    <div className="flex flex-col items-center min-w-[56px]">
                      <span className={cn('text-3xl font-extrabold tabular-nums', tc)}>{e.trustScore ?? '—'}</span>
                      <span className={cn('text-[10px] font-semibold', tc)}>{tLabel}</span>
                    </div>
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-fg-muted">{e.transactionId}</span>
                        <Badge tone={decisionTone(e.decision)}>{e.decision}</Badge>
                        {typeof e.signals?.['confidence'] === 'string' && (
                          <Badge tone={confidenceTone(e.signals['confidence'])}>
                            {e.signals['confidence']} confidence
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-fg-subtle">{fmt(e.createdAt)}{e.mobileNumberMasked ? ` · ${e.mobileNumberMasked}` : ''}</div>

                      {/* Positive factors */}
                      {posFactors.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {posFactors.map((f) => (
                            <span key={f} className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                              <CheckCircle className="h-2.5 w-2.5" />
                              {factorLabel(f)}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Risk factors */}
                      {riskFactors.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {riskFactors.map((f) => (
                            <span key={f} className="inline-flex items-center gap-0.5 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              {factorLabel(f)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Breakdown mini-chart */}
                    {breakdown && (
                      <div className="hidden sm:flex flex-col gap-0.5 text-right min-w-[120px]">
                        {(['verificationSuccessRatio','journeyStateBonus','recencyBonus','longevityBonus','deviceConsistencyBonus'] as const).map((key) => {
                          const val = breakdown[key];
                          if (val == null) return null;
                          const label = key.replace(/Bonus|Ratio/g, '').replace(/([A-Z])/g, ' $1').trim();
                          return (
                            <div key={key} className="flex items-center justify-end gap-1 text-[10px]">
                              <span className="text-fg-subtle">{label}</span>
                              <span className={cn('font-bold tabular-nums', val > 0 ? 'text-emerald-600 dark:text-emerald-400' : val < 0 ? 'text-rose-500' : 'text-fg-muted')}>
                                {val > 0 ? '+' : ''}{val}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Trust scale reference */}
      <Card>
        <CardHeader><CardTitle>Understanding the Trust Score</CardTitle></CardHeader>
        <div className="grid gap-3 sm:grid-cols-3 text-xs">
          {[
            { range: '70–100', label: 'Allow',  desc: 'Strong verification history, stable device and operator, recent successful verification.',  border: 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10' },
            { range: '40–69',  label: 'Review', desc: 'Moderate history or some inconsistency. Additional confirmation recommended.',              border: 'border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10'         },
            { range: '0–39',   label: 'Block',  desc: 'Insufficient history, unresolved journey, or multiple inconsistency signals.',              border: 'border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10'             },
          ].map(({ range, label, desc, border }) => (
            <div key={range} className={cn('rounded-xl border p-3', border)}>
              <p className="font-bold text-fg">{range}</p>
              <p className="mt-0.5 font-semibold text-fg">{label}</p>
              <p className="mt-1 text-fg-muted">{desc}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
